import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import classes from './scripts.module.scss';
import ScriptsFilters from './ScriptsFilters/ScriptsFilters';
import { useQuery } from '../../../utils/hooks/useQuery';
import accountHelpers from '../../../utils/accountHelpers';
import ScriptsStatusBar from './ScriptsStatusBar/ScriptsStatusBar';
import { changeMenuPath } from '../../../store/actions/menu';
import utils from '../../../utils/utils';
import { getScriptsRequest } from '../../../store/actions/script';
import ScriptTable from './ScriptTable/ScriptTable';
import Loader from '../../_common/Loader/Loader';

const tableHeader = ['Status', 'Date', 'Age', 'Note', 'Physician', 'Patient', 'Medication', 'Other'];

const defaultStatuses = ['Received', 'Review', 'Prior Auth'];

const Scripts = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { query, setQuery, queryToStringify } = useQuery();
  const { scriptData, scriptTotalPage, requestStatus } = useSelector((store) => store.script);

  const firstLoad = useRef(!query.status
    || (query.status.split(',').length === 3 && query.status.split(',').every((s) => defaultStatuses.includes(s))));

  const [requestData, setRequestData] = useState({
    page: query.page || 1,
    pouch: query.pouch ? JSON.parse(query.pouch) : '',
    userId: accountHelpers.getUser().id,
    specialization: query.specialization || '',
    rep: query.rep || '',
    limit: query.limit || 15,
    textSearch: query.textSearch || '',
    homeCare: query.homeCare || '',
    salesCode: query.salesCode || '',
    location: query.location || '',
    patientLocation: query.patientLocation || '',
    assign: query.assign || '',
    status: query.status ? query.status.split(',') : defaultStatuses,
  });

  const { limit, page } = requestData;

  useEffect(() => {
    const newQuery = utils.deleteEmptyKeys({ ...requestData });

    delete newQuery.userId;

    if (!_.isEmpty(newQuery.status)) {
      newQuery.status = newQuery.status.join(',');
    } else {
      delete newQuery.status;
    }

    dispatch(changeMenuPath('Scripts', `${pathname}${queryToStringify({
      ...newQuery,
      textSearch: requestData.status.some((s) => ['QA', 'Fill'].includes(s)) ? '' : newQuery.textSearch,
    })}`));
    setQuery(newQuery);
  }, [requestData]);

  useEffect(() => {
    const newQuery = utils.deleteEmptyKeys({ ...requestData });

    if (!_.isEmpty(newQuery.status)) {
      newQuery.status = newQuery.status.join(',');
    } else {
      delete newQuery.status;
    }

    (async () => {
      try {
        await dispatch(getScriptsRequest({ ...newQuery, cancelToken: 'getScriptsData' }));
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const changeStatus = async (name) => {
    const status = firstLoad.current
      ? [name]

      : requestData.status.includes(name)
        ? requestData.status.filter((d) => d !== name)

        : [...requestData.status, name];

    setRequestData((prev) => ({
      ...prev,
      status,
      page: 1,
    }));

    const newQuery = utils.deleteEmptyKeys({ ...requestData, status, page: 1 });

    if (!_.isEmpty(newQuery.status)) {
      newQuery.status = newQuery.status.join(',');
    } else delete newQuery.status;

    try {
      await dispatch(getScriptsRequest({ ...newQuery, cancelToken: 'getScriptsData' }));

      firstLoad.current = false;
    } catch (e) {
      console.log(e);
    }
  };

  const firstRequest = async (type, value) => {
    let newQuery;

    if (requestData[type] === value) {
      setRequestData((prev) => ({ ...prev, [type]: '', page: 1 }));
      newQuery = utils.deleteEmptyKeys({ ...requestData, [type]: '', page: 1 });
    } else {
      setRequestData((prev) => ({ ...prev, [type]: value, page: 1 }));
      newQuery = utils.deleteEmptyKeys({ ...requestData, [type]: value, page: 1 });
    }

    if (!_.isEmpty(newQuery.status)) {
      newQuery.status = newQuery.status.join(',');
    } else delete newQuery.status;

    try {
      await dispatch(getScriptsRequest({ ...newQuery, cancelToken: 'getScriptsData' }));
    } catch (e) {
      console.log(e);
    }
  };

  const onChangePageData = async (path, count) => {
    const newQuery = utils.deleteEmptyKeys({ ...requestData, [path]: count });

    if (path === 'limit') {
      newQuery.page = 1;
    }

    setRequestData({ ...newQuery });

    if (!_.isEmpty(newQuery.status)) {
      newQuery.status = newQuery.status.join(',');
    } else delete newQuery.status;

    try {
      await dispatch(getScriptsRequest({ ...newQuery, cancelToken: 'getScriptsData' }));
    } catch (e) {
      console.log(e);
    }
  };

  const clearFilters = async () => {
    const clearData = {
      page: 1,
      userId: accountHelpers.getUser().id,
      specialization: '',
      rep: '',
      limit: 15,
      textSearch: '',
      homeCare: '',
      salesCode: '',
      location: '',
      patientLocation: '',
      assign: '',
      status: [],
    };

    setRequestData(clearData);

    const newQuery = utils.deleteEmptyKeys(clearData);

    try {
      await dispatch(getScriptsRequest({ ...newQuery, cancelToken: 'getScriptsData' }));
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className={classes.scripts_wrapper}>
      <ScriptsFilters
        requestData={requestData}
        firstRequest={firstRequest}
        clearFilters={clearFilters}
      />

      <ScriptsStatusBar
        onClick={changeStatus}
        activeStatuses={requestData.status}
      />

      <div className={classes.load_wrapper}>
        {requestStatus === 'request'
          ? <Loader />

          : (
            <ScriptTable
              data={scriptData}
              header={tableHeader.filter((t) => (+accountHelpers.getUser().id === 601
                ? (t !== 'Physician' && t !== 'Patient')
                : t))}
              totalPage={+scriptTotalPage}
              page={+page - 1}
              limit={+limit}
              className={classes.table}
              onChange={onChangePageData}
            />
          )}
      </div>
    </div>
  );
};

export default Scripts;
